import { useCallback, useEffect, useRef, useState, KeyboardEvent } from 'react';
import axios from 'axios';
import useUserDetails from '../../hooks/useUserDetails';
import useErrorModal from '../../hooks/useErrorModal';
import {
  ChatConversationSummary,
  ChatModelOption,
  getChatConversation,
  getChatModels,
  listChatConversations,
  postChatStream,
} from '../_shared/chatService';
import DraftBody from '../_shared/DraftBody';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatVariant {
  defaultScope: string;
  scopeStorageKey: string;
  modelStorageKey: string;
  title: string;
  subtitle: string;
  enableSql: boolean;
}

const SQL_CHAT_VARIANT: ChatVariant = {
  defaultScope: 'admin-sql',
  scopeStorageKey: 'prototype4-sql-chat-scope',
  modelStorageKey: 'prototype4-sql-chat-model',
  title: 'SQL Chat (super admin)',
  subtitle: 'Live assistant with direct read-only SQL access',
  enableSql: true,
};

const TOOL_LABELS: Record<string, string> = {
  metrics_catalog: 'Looking up available metrics…',
  query_metrics: 'Querying district metrics…',
  visualizations_catalog: 'Looking up visualizations…',
  query_visualizations: 'Preparing visualization…',
  survey_metrics_catalog: 'Looking up survey metrics…',
  query_survey_metrics: 'Querying survey metrics…',
  get_survey_response_stats: 'Checking survey response counts…',
  list_survey_questions: 'Loading survey questions…',
  get_survey_question_answer_breakdown: 'Analyzing survey answers…',
  survey_visualizations_catalog: 'Looking up survey visualizations…',
  query_survey_visualizations: 'Preparing survey visualization…',
  search_courses: 'Searching the course catalog…',
  list_focus_group_transcriptions: 'Listing focus group transcriptions…',
  get_focus_group_transcription: 'Reading focus group transcript…',
  execute_sql: 'Running SQL…',
};

const toolLabel = (toolName: string): string => TOOL_LABELS[toolName] ?? `Using ${toolName}…`;

const TAILWIND_SCRIPT_ID = 'prototypes-tailwind-cdn';
const INTER_LINK_ID = 'prototypes-inter-font';
const HIDE_APP_CHROME_STYLE_ID = 'prototypes-hide-app-chrome';

const ChatConversation = ({ variant }: { variant: ChatVariant }) => {
  const readStoredScope = (): string => window.localStorage.getItem(variant.scopeStorageKey) ?? variant.defaultScope;
  const readStoredModel = (): string => window.localStorage.getItem(variant.modelStorageKey) ?? '';

  const [scope, setScope] = useState<string>(readStoredScope);
  const [modelId, setModelId] = useState<string>(readStoredModel);
  const [availableModels, setAvailableModels] = useState<ChatModelOption[]>([]);
  const [conversations, setConversations] = useState<ChatConversationSummary[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [input, setInput] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState<string | null>(null);
  const [thinkingText, setThinkingText] = useState<string | null>(null);
  const [streamingToolLabel, setStreamingToolLabel] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  useEffect(() => {
    const eoaId = userDetails?.eoaId;
    const accessToken = userDetails?.access_token;
    if (!eoaId || !accessToken) return;

    void (async () => {
      try {
        const data = await getChatConversation(eoaId, scope, accessToken);
        setMessages(data.display_messages.map((m) => ({ role: m.role, content: m.content })));
        setConversationId(data.conversation_id);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          setMessages([]);
          setConversationId(undefined);
          return;
        }
        handleOpenErrorModal('Failed to load conversation', error as Error);
      }
    })();
  }, [userDetails?.eoaId, userDetails?.access_token, scope, handleOpenErrorModal]);

  const refreshConversations = useCallback(async () => {
    const eoaId = userDetails?.eoaId;
    const accessToken = userDetails?.access_token;
    if (!eoaId || !accessToken) return;
    try {
      const data = await listChatConversations(eoaId, accessToken, variant.defaultScope);
      setConversations(data.conversations);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        setConversations([]);
        return;
      }
      handleOpenErrorModal('Failed to load conversations', error as Error);
    }
  }, [userDetails?.eoaId, userDetails?.access_token, handleOpenErrorModal]);

  useEffect(() => {
    void refreshConversations();
  }, [refreshConversations]);

  useEffect(() => {
    const eoaId = userDetails?.eoaId;
    const accessToken = userDetails?.access_token;
    if (!eoaId || !accessToken) return;
    void (async () => {
      try {
        const data = await getChatModels(eoaId, accessToken);
        setAvailableModels(data.available_models);
        setModelId((current) => current || data.default_model_id);
      } catch (error) {
        handleOpenErrorModal('Failed to load chat models', error as Error);
      }
    })();
  }, [userDetails?.eoaId, userDetails?.access_token, handleOpenErrorModal]);

  const selectModel = (nextModelId: string) => {
    window.localStorage.setItem(variant.modelStorageKey, nextModelId);
    setModelId(nextModelId);
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, isPending, streamingMessage, thinkingText, streamingToolLabel]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const startNewThread = () => {
    abortRef.current?.abort();
    const nextScope = `${variant.defaultScope}-${crypto.randomUUID()}`;
    window.localStorage.setItem(variant.scopeStorageKey, nextScope);
    setMessages([]);
    setConversationId(undefined);
    setInput('');
    setStreamingMessage(null);
    setThinkingText(null);
    setStreamingToolLabel(null);
    setIsPending(false);
    setScope(nextScope);
  };

  const selectConversation = (nextScope: string) => {
    if (nextScope === scope) return;
    abortRef.current?.abort();
    window.localStorage.setItem(variant.scopeStorageKey, nextScope);
    setInput('');
    setStreamingMessage(null);
    setThinkingText(null);
    setStreamingToolLabel(null);
    setIsPending(false);
    setScope(nextScope);
  };

  const send = async () => {
    const trimmed = input.trim();
    if (!trimmed || isPending) return;

    const eoaId = userDetails?.eoaId;
    const accessToken = userDetails?.access_token;
    if (!eoaId || !accessToken) {
      handleOpenErrorModal('Missing EOA or access token', new Error('User details unavailable'));
      return;
    }

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setIsPending(true);
    setStreamingMessage(null);
    setThinkingText(null);
    setStreamingToolLabel(null);

    const controller = new AbortController();
    abortRef.current = controller;
    // The backend tags text before any tool call as "thinking" (narration) and text after the
    // tools have run as "delta" (the answer). Thinking + tool activity render in the gray line;
    // the answer streams token-by-token straight into the white bubble.
    let answer = '';
    let narration = '';

    try {
      const { conversation_id, response } = await postChatStream(
        {
          prompt: trimmed,
          eoa_id: eoaId,
          scope,
          conversation_id: conversationId,
          mode: 'real',
          model_id: modelId || undefined,
          enable_sql: variant.enableSql || undefined,
        },
        accessToken,
        {
          signal: controller.signal,
          onDelta: (text) => {
            answer += text;
            setThinkingText(null);
            setStreamingToolLabel(null);
            setStreamingMessage(answer);
          },
          onThinking: (text) => {
            narration += text;
            setStreamingToolLabel(null);
            setThinkingText(narration);
          },
          onToolCall: (toolName) => {
            narration = '';
            answer = '';
            setThinkingText(null);
            setStreamingMessage(null);
            setStreamingToolLabel(toolLabel(toolName));
          },
          onToolResult: () => setStreamingToolLabel(null),
        }
      );
      setConversationId(conversation_id);
      setMessages((prev) => [...prev, { role: 'assistant', content: response }]);
      void refreshConversations();
    } catch (error) {
      if (!controller.signal.aborted) {
        handleOpenErrorModal('Chat request failed', error as Error);
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      if (!controller.signal.aborted) {
        setStreamingMessage(null);
        setThinkingText(null);
        setStreamingToolLabel(null);
        setIsPending(false);
      }
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <div className="flex flex-col w-full h-full bg-[#fafafa] font-['Inter']">
      <header className="flex items-center justify-between border-b border-[#e5e5e5] bg-white px-6 py-4">
        <div>
          <h1 className="text-[16px] font-600 text-[#0a0a0a]">{variant.title}</h1>
          <p className="text-[12px] text-[#737373]">
            {variant.subtitle} · scope {scope}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={modelId}
            onChange={(e) => selectModel(e.target.value)}
            aria-label="Model"
            disabled={availableModels.length === 0}
            className="max-w-[200px] rounded-md border border-[#e5e5e5] bg-white px-3 py-1.5 text-[13px] font-500 text-[#0a0a0a] hover:bg-[#f5f5f5] focus:border-[#0a0a0a] focus:outline-none disabled:opacity-40"
          >
            {availableModels.map((model) => (
              <option key={model.id} value={model.id}>
                {model.label}
              </option>
            ))}
          </select>
          <select
            value={scope}
            onChange={(e) => selectConversation(e.target.value)}
            aria-label="Previous conversations"
            className="max-w-[260px] rounded-md border border-[#e5e5e5] bg-white px-3 py-1.5 text-[13px] font-500 text-[#0a0a0a] hover:bg-[#f5f5f5] focus:border-[#0a0a0a] focus:outline-none"
          >
            {!conversations.some((c) => c.scope === scope) && <option value={scope}>New conversation</option>}
            {conversations.map((c) => (
              <option key={c.scope} value={c.scope}>
                {c.title}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={startNewThread}
            className="rounded-md border border-[#e5e5e5] bg-white px-3 py-1.5 text-[13px] font-500 text-[#0a0a0a] hover:bg-[#f5f5f5]"
          >
            New thread
          </button>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mx-auto flex max-w-[760px] flex-col gap-4">
          {messages.length === 0 && !isPending && (
            <div className="mt-16 text-center text-[14px] text-[#a3a3a3]">
              Ask anything about this district&apos;s data to get started.
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'user' ? (
                <div className="max-w-[80%] whitespace-pre-wrap rounded-2xl bg-[#0a0a0a] px-4 py-2.5 text-[14px] leading-relaxed text-white">
                  {m.content}
                </div>
              ) : (
                <div className="max-w-[80%] rounded-2xl border border-[#e5e5e5] bg-white px-4 py-2.5">
                  <DraftBody text={m.content} />
                </div>
              )}
            </div>
          ))}
          {isPending && streamingMessage === null && (
            <div className="flex justify-start">
              <div className="flex max-w-[80%] items-start gap-2 py-0.5 text-[13px] italic text-[#a3a3a3]">
                <span className="mt-1.5 inline-flex shrink-0 gap-1">
                  <span
                    className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#d4d4d4]"
                    style={{ animationDelay: '-0.3s' }}
                  />
                  <span
                    className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#d4d4d4]"
                    style={{ animationDelay: '-0.15s' }}
                  />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#d4d4d4]" />
                </span>
                <span className="whitespace-pre-wrap">{streamingToolLabel ?? thinkingText ?? 'Thinking…'}</span>
              </div>
            </div>
          )}
          {streamingMessage !== null && (
            <div className="flex justify-start">
              <div className="max-w-[80%] rounded-2xl border border-[#e5e5e5] bg-white px-4 py-2.5">
                <DraftBody text={streamingMessage} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-[#e5e5e5] bg-white px-6 py-4">
        <div className="mx-auto flex max-w-[760px] items-end gap-3">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder="Message the assistant…"
            className="flex-1 resize-none rounded-lg border border-[#e5e5e5] px-3 py-2.5 text-[14px] text-[#0a0a0a] outline-none focus:border-[#0a0a0a]"
          />
          <button
            type="button"
            onClick={() => void send()}
            disabled={isPending || !input.trim()}
            className="rounded-lg bg-[#0a0a0a] px-4 py-2.5 text-[14px] font-500 text-white disabled:opacity-40"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

const Prototype4Page = () => {
  useEffect(() => {
    const tw = document.createElement('script');
    tw.id = TAILWIND_SCRIPT_ID;
    tw.src = 'https://cdn.tailwindcss.com';
    tw.onload = () => {
      const w = window as unknown as { tailwind?: { config?: Record<string, unknown> } };
      if (w.tailwind) {
        w.tailwind.config = { corePlugins: { preflight: false } };
      }
    };

    const inter = document.createElement('link');
    inter.id = INTER_LINK_ID;
    inter.rel = 'stylesheet';
    inter.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';

    const hideAppChrome = document.createElement('style');
    hideAppChrome.id = HIDE_APP_CHROME_STYLE_ID;
    hideAppChrome.textContent = '.footer { display: none !important; }';

    document.head.append(tw, inter, hideAppChrome);

    return () => {
      document.getElementById(TAILWIND_SCRIPT_ID)?.remove();
      document.getElementById(INTER_LINK_ID)?.remove();
      document.getElementById(HIDE_APP_CHROME_STYLE_ID)?.remove();
    };
  }, []);

  return (
    <div className="tw relative w-full h-full overflow-hidden">
      <ChatConversation variant={SQL_CHAT_VARIANT} />
    </div>
  );
};

export default Prototype4Page;
